import { useState, useEffect, Fragment } from "react";
import { EmptyState } from "@/components/EmptyState";
import { Panel } from "@/components/Panel";
import { useHistoryMetrics, useWeeklyHistoryMetrics } from "@/lib/api";
import { safeRatio, formatPercent, formatCurrency } from "@/lib/format";
import type { HistoryResponse } from "@/lib/types";

interface MonthQcStat {
  monthKey: string;
  label: string;
  totalSqls: number;
  callsFound: number;
  rawRecordings: number;
  highQuality: number;
  avgFoundScore: number;
}

function FathomSqlQualityComparison({ months }: { months: { month: string; label: string; sqlCount?: number }[] }) {
  const [qcStats, setQcStats] = useState<MonthQcStat[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    
    Promise.all(
      months.map(async (m) => {
        const norm = m.month.replace('/', '-');
        try {
          const res = await fetch(`/api/fathom/quality-check?month=${norm}`);
          if (!res.ok) return null;
          const data = await res.json();
          const results = data.results || [];
          const found = results.filter(
            (r: any) => r.fathom_meeting_id || r.fathom_recording_url || r.quality_band === "High"
          );
          const highQuality = found.filter(
            (r: any) => r.quality_band === "High" || (r.overall_quality_score || 0) >= 8
          ).length;
          const scores = found
            .map((r: any) => r.overall_quality_score)
            .filter((s: any) => typeof s === "number");
          const avgFoundScore =
            scores.length > 0 ? scores.reduce((a: number, b: number) => a + b, 0) / scores.length : 0;

          return {
            monthKey: m.month,
            label: m.label,
            totalSqls: m.sqlCount != null ? m.sqlCount : results.length,
            callsFound: found.length,
            rawRecordings: results.length,
            highQuality,
            avgFoundScore: Math.round(avgFoundScore * 10) / 10,
          };
        } catch (_) {
          return null;
        }
      })
    ).then((items) => {
      if (isMounted) {
        setQcStats(items.filter(Boolean) as MonthQcStat[]);
        setLoading(false);
      }
    });

    return () => {
      isMounted = false;
    };
  }, [months]);

  // Overall totals across all months
  const totalAllSqls = qcStats.reduce((acc, curr) => acc + curr.totalSqls, 0);
  const totalCallsFound = qcStats.reduce((acc, curr) => acc + curr.callsFound, 0);
  const totalHighQuality = qcStats.reduce((acc, curr) => acc + curr.highQuality, 0);
  const validAvgScores = qcStats.map((s) => s.avgFoundScore).filter((s) => s > 0);
  const overallAvgScore =
    validAvgScores.length > 0
      ? Math.round((validAvgScores.reduce((a, b) => a + b, 0) / validAvgScores.length) * 10) / 10
      : 0;

  return (
    <Panel
      title="SQL Quality Comparison (Fathom AI)"
      subtitle="Month-by-month breakdown of demo call recordings, match rates, and AI quality scores"
    >
      {loading ? (
        <div className="text-xs text-slate-500 italic py-2">Loading Fathom quality comparison...</div>
      ) : qcStats.length === 0 ? (
        <div className="text-xs text-slate-500 italic py-2">No Fathom quality data found for these months.</div>
      ) : (
        <div className="overflow-x-auto -mx-5 pb-2">
          <table className="w-full text-xs border-collapse">
            <thead>
              <tr className="bg-slate-50 text-slate-700 border-b border-slate-200">
                <th className="font-semibold px-5 py-2.5 text-left sticky left-0 z-20 bg-slate-50 border-r border-slate-200">Metric</th>
                {qcStats.map((m) => (
                  <th key={m.monthKey} className="font-semibold px-4 py-2.5 text-center border-r border-slate-200">
                    {m.label}
                  </th>
                ))}
                <th className="font-semibold px-4 py-2.5 text-center bg-cyan-50/70 text-cyan-900">Overall</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-slate-100 hover:bg-slate-50/50">
                <td className="px-5 py-2.5 font-medium text-slate-800 sticky left-0 bg-white border-r border-slate-200">Total SQLs</td>
                {qcStats.map((m) => (
                  <td key={m.monthKey} className="px-4 py-2.5 text-center text-slate-700 font-semibold border-r border-slate-200">
                    {m.totalSqls}
                  </td>
                ))}
                <td className="px-4 py-2.5 text-center text-cyan-900 font-bold bg-cyan-50/40">{totalAllSqls}</td>
              </tr>
              <tr className="border-b border-slate-100 hover:bg-slate-50/50">
                <td className="px-5 py-2.5 font-medium text-slate-800 sticky left-0 bg-white border-r border-slate-200">Calls Matched (Unique SQLs)</td>
                {qcStats.map((m) => (
                  <td key={m.monthKey} className="px-4 py-2.5 text-center text-blue-700 font-semibold border-r border-slate-200">
                    {m.callsFound} / {m.totalSqls}
                  </td>
                ))}
                <td className="px-4 py-2.5 text-center text-blue-800 font-bold bg-cyan-50/40">
                  {totalCallsFound} / {totalAllSqls}
                </td>
              </tr>
              <tr className="border-b border-slate-100 hover:bg-slate-50/50">
                <td className="px-5 py-2.5 font-medium text-slate-800 sticky left-0 bg-white border-r border-slate-200">Total Fathom Recordings Logged</td>
                {qcStats.map((m) => (
                  <td key={m.monthKey} className="px-4 py-2.5 text-center text-purple-700 font-semibold border-r border-slate-200">
                    {m.rawRecordings || m.callsFound} recordings
                  </td>
                ))}
                <td className="px-4 py-2.5 text-center text-purple-800 font-bold bg-cyan-50/40">
                  {qcStats.reduce((a, b) => a + (b.rawRecordings || b.callsFound), 0)} recordings
                </td>
              </tr>
              <tr className="border-b border-slate-100 hover:bg-slate-50/50">
                <td className="px-5 py-2.5 font-medium text-slate-800 sticky left-0 bg-white border-r border-slate-200">High Quality Demos</td>
                {qcStats.map((m) => (
                  <td key={m.monthKey} className="px-4 py-2.5 text-center text-emerald-700 font-semibold border-r border-slate-200">
                    {m.highQuality}
                  </td>
                ))}
                <td className="px-4 py-2.5 text-center text-emerald-800 font-bold bg-cyan-50/40">{totalHighQuality}</td>
              </tr>
              <tr className="border-b border-slate-200 bg-slate-50/60 font-semibold">
                <td className="px-5 py-2.5 font-semibold text-slate-900 sticky left-0 bg-slate-50 border-r border-slate-200">Avg Call Quality Score</td>
                {qcStats.map((m) => (
                  <td key={m.monthKey} className="px-4 py-2.5 text-center border-r border-slate-200">
                    {m.avgFoundScore > 0 ? (
                      <span className="inline-block px-2 py-0.5 rounded bg-emerald-100 text-emerald-800 font-bold">
                        {m.avgFoundScore}/10
                      </span>
                    ) : (
                      <span className="text-slate-400">—</span>
                    )}
                  </td>
                ))}
                <td className="px-4 py-2.5 text-center bg-cyan-100/60">
                  <span className="inline-block px-2.5 py-0.5 rounded bg-cyan-200 text-cyan-900 font-bold text-xs">
                    {overallAvgScore}/10
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  );
}

function normalizeComparisonSource(source: string): string {
  const value = (source || "").trim().toLowerCase();

  if (["direct", "chat", "phone inquiry"].includes(value)) return "Direct";
  if (["email"].includes(value)) return "Email";
  if (["organic search"].includes(value)) return "Organic Search";
  if (["paid search"].includes(value)) return "Paid Search";
  if (["paid social", "facebook", "hellosprout"].includes(value)) return "Paid Social";
  if (["referral", "merchant referral"].includes(value)) return "Referral";
  if (
    [
      "gtm organic",
      "lead magnets",
      "organic",
      "ai website builder email entry",
      "digital menu web sign-up",
      "chatgpt.com / ai-assistant",
      "ai website builder sign-up"
    ].includes(value)
  ) {
    return "GTM Organic";
  }
  if (["to be identified", "unidentified", "marketplace / (not set)"].includes(value)) return "Direct";

  return "Direct";
}

const COMPARISON_SOURCE_ORDER = [
  "Direct",
  "Email",
  "Organic Search",
  "Paid Search",
  "Paid Social",
  "Referral",
  "GTM Organic",
];

function exportToCSV(filename: string, headers: string[], rows: (string | number)[][]) {
  const csvContent = [
    headers.map(h => `"${String(h).replace(/"/g, '""')}"`).join(","),
    ...rows.map(row => row.map(cell => `"${String(cell ?? "").replace(/"/g, '""')}"`).join(","))
  ].join("\n");

  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.setAttribute("href", url);
  link.setAttribute("download", filename);
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

export function Comparison() {
  const [mode, setMode] = useState<"monthly" | "weekly">("monthly");
  const [months, setMonths] = useState(6);
  const [weeks, setWeeks] = useState(4);
  
  const { data: monthlyData, isLoading: isMonthlyLoading } = useHistoryMetrics(months);
  const { data: weeklyData, isLoading: isWeeklyLoading } = useWeeklyHistoryMetrics(weeks);

  const data = mode === "monthly" ? monthlyData : weeklyData;
  const isLoading = mode === "monthly" ? isMonthlyLoading : isWeeklyLoading;

  if (!data) return <EmptyState title="Loading comparison data" loading={isLoading} />;

  const historyData: HistoryResponse = data;
  if (!historyData.months || historyData.months.length === 0) {
    return <EmptyState title="No historical data available" loading={false} />;
  }

  // Pre-process history data to group sources and calculate totals
  const displayMonths = [...historyData.months];
  const groupedMonths = displayMonths.map((m) => {
    const groupedSources: Record<string, { mql: number; sql: number; sqo: number; wins: number }> = {};

    m.sources.forEach((s) => {
      const norm = normalizeComparisonSource(s.source);
      if (!groupedSources[norm]) {
        groupedSources[norm] = { mql: 0, sql: 0, sqo: 0, wins: 0 };
      }
      groupedSources[norm].mql += s.mql || 0;
      groupedSources[norm].sql += s.sql || 0;
      groupedSources[norm].sqo += s.sqo || 0;
      groupedSources[norm].wins += s.wins || 0;
    });

    const totals = { mql: 0, sql: 0, sqo: 0, wins: 0 };
    Object.values(groupedSources).forEach((s) => {
      totals.mql += s.mql;
      totals.sql += s.sql;
      totals.sqo += s.sqo;
      totals.wins += s.wins;
    });

    return {
      ...m,
      groupedSources,
      calculatedTotals: totals
    };
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl font-semibold text-ink-900">
          {mode === "monthly" ? "Past Month Comparison" : "Past Week Comparison"}
        </h1>
        <p className="text-sm text-slate-500 mt-0.5">
          Compare source performance across previous {mode === "monthly" ? "months" : "weeks"}
        </p>
      </div>

      <div className="flex items-center gap-6">
        <div className="flex items-center gap-1 bg-slate-100 p-1 rounded-lg">
          <button
            onClick={() => setMode("monthly")}
            className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
              mode === "monthly"
                ? "bg-white text-ink-900 shadow-sm"
                : "text-slate-600 hover:text-ink-900 hover:bg-slate-200/50"
            }`}
          >
            Monthly
          </button>
          <button
            onClick={() => setMode("weekly")}
            className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
              mode === "weekly"
                ? "bg-white text-ink-900 shadow-sm"
                : "text-slate-600 hover:text-ink-900 hover:bg-slate-200/50"
            }`}
          >
            Weekly
          </button>
        </div>

        <div className="flex items-center gap-3">
          <label className="text-sm font-medium text-slate-700">Time Range:</label>
          {mode === "monthly" ? (
            <select
              value={months}
              onChange={(e) => setMonths(Number(e.target.value))}
              className="text-sm rounded-md border border-border px-3 py-1.5 focus:outline-none focus:ring-1 focus:ring-accent-blue bg-white"
            >
              <option value={3}>Last 3 months</option>
              <option value={6}>Last 6 months</option>
              <option value={12}>Last 12 months</option>
            </select>
          ) : (
            <select
              value={weeks}
              onChange={(e) => setWeeks(Number(e.target.value))}
              className="text-sm rounded-md border border-border px-3 py-1.5 focus:outline-none focus:ring-1 focus:ring-accent-blue bg-white"
            >
              <option value={4}>Last 4 weeks</option>
              <option value={8}>Last 8 weeks</option>
              <option value={12}>Last 12 weeks</option>
            </select>
          )}
        </div>
      </div>


      {(() => {
        const downloadMonthlyPerformanceCSV = () => {
          const headers = ["Source"];
          groupedMonths.forEach(m => {
            headers.push(`${m.label} MQL`, `${m.label} SQL`, `${m.label} SQO`, `${m.label} Won`);
          });

          const rows: (string | number)[][] = COMPARISON_SOURCE_ORDER.map(sourceName => {
            const row: (string | number)[] = [sourceName];
            groupedMonths.forEach(m => {
              const src = m.groupedSources[sourceName];
              row.push(src?.mql || 0, src?.sql || 0, src?.sqo || 0, src?.wins || 0);
            });
            return row;
          });

          const totalsRow: (string | number)[] = ["Total"];
          groupedMonths.forEach(m => {
            totalsRow.push(m.calculatedTotals.mql || 0, m.calculatedTotals.sql || 0, m.calculatedTotals.sqo || 0, m.calculatedTotals.wins || 0);
          });
          rows.push(totalsRow);

          const sqlRow: (string | number)[] = ["%SQL"];
          groupedMonths.forEach(m => {
            const mql = m.calculatedTotals.mql || 0;
            const sql = m.calculatedTotals.sql || 0;
            const ratio = safeRatio(sql, mql);
            const val = ratio != null ? formatPercent(ratio) : "—";
            sqlRow.push(val, "", "", "");
          });
          rows.push(sqlRow);

          const wonSqlRow: (string | number)[] = ["Won / SQL"];
          groupedMonths.forEach(m => {
            const sql = m.calculatedTotals.sql || 0;
            const wins = m.calculatedTotals.wins || 0;
            const ratio = safeRatio(wins, sql);
            const val = ratio != null ? formatPercent(ratio) : "—";
            wonSqlRow.push(val, "", "", "");
          });
          rows.push(wonSqlRow);

          const mqlWonRow: (string | number)[] = ["MQL / Won"];
          groupedMonths.forEach(m => {
            const mql = m.calculatedTotals.mql || 0;
            const wins = m.calculatedTotals.wins || 0;
            const ratio = wins > 0 ? (mql / wins).toFixed(1) : "—";
            mqlWonRow.push(ratio, "", "", "");
          });
          rows.push(mqlWonRow);

          exportToCSV("monthly_performance_by_source.csv", headers, rows);
        };

        return (
          <Panel
            title={mode === "monthly" ? "Monthly Performance by Source" : "Weekly Performance by Source"}
            right={
              <button
                onClick={downloadMonthlyPerformanceCSV}
                className="px-2.5 py-1.5 text-xs font-medium text-slate-700 bg-white border border-slate-300 rounded-md hover:bg-slate-50 transition-colors inline-flex items-center gap-1.5"
              >
                <svg className="w-3.5 h-3.5 text-slate-500" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
                </svg>
                Export CSV
              </button>
            }
          >
            <div className="overflow-x-auto -mx-5 pb-4">
              <table className="w-full text-sm border-collapse min-w-max">
                <thead>
                  <tr>
                    <th className="font-semibold px-5 py-2.5 text-left border-b border-r-2 border-slate-300 bg-slate-50 sticky left-0 z-20">
                      Source
                    </th>
                    {groupedMonths.map((m, i) => {
                      const isHighlightedMonth = i % 2 === 0;
                      const monthHeaderBg = isHighlightedMonth ? "bg-blue-100/60" : "bg-slate-50";
                      return (
                        <th key={m.month} colSpan={4} className={`font-semibold px-5 py-2.5 text-center border-b border-r-2 border-slate-300 ${monthHeaderBg} text-slate-800`}>
                          {m.label}
                        </th>
                      );
                    })}
                  </tr>
                  <tr className="label-xs text-slate-500 bg-slate-50">
                    <th className="font-medium px-5 py-2 border-b border-r-2 border-slate-300 sticky left-0 bg-slate-50 z-20"></th>
                    {groupedMonths.map((m, i) => {
                      const isHighlightedMonth = i % 2 === 0;
                      const subHeaderBg = isHighlightedMonth ? "bg-blue-50/40" : "bg-white";
                      return (
                        <Fragment key={`headers-${m.month}`}>
                          <th className={`font-semibold px-3 py-2 text-right border-b border-slate-300 text-slate-600 ${subHeaderBg}`}>MQL</th>
                          <th className={`font-semibold px-3 py-2 text-right border-b border-slate-300 text-slate-600 ${subHeaderBg}`}>SQL</th>
                          <th className={`font-semibold px-3 py-2 text-right border-b border-slate-300 text-slate-600 ${subHeaderBg}`}>SQO</th>
                          <th className={`font-semibold px-3 py-2 text-right border-b border-r-2 border-slate-300 text-slate-600 ${subHeaderBg}`}>Won</th>
                        </Fragment>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {COMPARISON_SOURCE_ORDER.map((sourceName) => (
                    <tr key={sourceName} className="border-b border-slate-100 hover:bg-slate-50 group">
                      <td className="px-5 py-2.5 font-medium text-slate-700 sticky left-0 bg-white border-r-2 border-slate-200 group-hover:bg-slate-50 z-20">
                        {sourceName}
                      </td>
                      {groupedMonths.map((m, i) => {
                        const src = m.groupedSources[sourceName];
                        const isHighlightedMonth = i % 2 === 0;
                        const cellBg = isHighlightedMonth ? "bg-blue-50/40 group-hover:bg-blue-100/50" : "bg-transparent";
                        return (
                          <Fragment key={`${sourceName}-${m.month}`}>
                            <td className={`px-3 py-2 text-right numeric-value text-slate-600 ${cellBg}`}>{src?.mql || 0}</td>
                            <td className={`px-3 py-2 text-right numeric-value text-slate-600 ${cellBg}`}>{src?.sql || 0}</td>
                            <td className={`px-3 py-2 text-right numeric-value text-slate-600 ${cellBg}`}>{src?.sqo || 0}</td>
                            <td className={`px-3 py-2 text-right numeric-value text-slate-600 border-r-2 border-slate-200 font-semibold ${cellBg}`}>{src?.wins || 0}</td>
                          </Fragment>
                        );
                      })}
                    </tr>
                  ))}

                  {/* Totals Row */}
                  <tr className="border-t-2 border-slate-300">
                    <td className="px-5 py-3 font-semibold text-ink-900 sticky left-0 bg-slate-100 border-r-2 border-slate-300 z-20">
                      Total
                    </td>
                    {groupedMonths.map((m, i) => {
                      const isHighlightedMonth = i % 2 === 0;
                      const totalBg = isHighlightedMonth ? "bg-blue-100/60" : "bg-slate-100";
                      return (
                        <Fragment key={`totals-${m.month}`}>
                          <td className={`px-3 py-3 text-right numeric-value font-semibold text-ink-900 ${totalBg}`}>{m.calculatedTotals.mql || 0}</td>
                          <td className={`px-3 py-3 text-right numeric-value font-semibold text-ink-900 ${totalBg}`}>{m.calculatedTotals.sql || 0}</td>
                          <td className={`px-3 py-3 text-right numeric-value font-semibold text-ink-900 ${totalBg}`}>{m.calculatedTotals.sqo || 0}</td>
                          <td className={`px-3 py-3 text-right numeric-value font-semibold text-ink-900 border-r-2 border-slate-300 ${totalBg}`}>{m.calculatedTotals.wins || 0}</td>
                        </Fragment>
                      );
                    })}
                  </tr>

                  {/* Ratio Rows */}
                  <tr className="border-b border-slate-100 text-xs">
                    <td className="px-5 py-2 font-medium text-slate-600 sticky left-0 bg-slate-50 border-r-2 border-slate-200 z-20">
                      %SQL
                    </td>
                    {groupedMonths.map((m, i) => {
                      const mql = m.calculatedTotals.mql || 0;
                      const sql = m.calculatedTotals.sql || 0;
                      const ratio = safeRatio(sql, mql);
                      const isHighlightedMonth = i % 2 === 0;
                      const ratioBg = isHighlightedMonth ? "bg-blue-50/30" : "bg-slate-50/60";
                      return (
                        <Fragment key={`ratio1-${m.month}`}>
                          <td colSpan={4} className={`px-3 py-2 text-center numeric-value text-slate-500 border-r-2 border-slate-200 ${ratioBg}`}>
                            {ratio != null ? formatPercent(ratio) : "—"}
                          </td>
                        </Fragment>
                      );
                    })}
                  </tr>
                  <tr className="border-b border-slate-100 text-xs">
                    <td className="px-5 py-2 font-medium text-slate-600 sticky left-0 bg-slate-50 border-r-2 border-slate-200 z-20">
                      Won / SQL
                    </td>
                    {groupedMonths.map((m, i) => {
                      const sql = m.calculatedTotals.sql || 0;
                      const wins = m.calculatedTotals.wins || 0;
                      const ratio = safeRatio(wins, sql);
                      const isHighlightedMonth = i % 2 === 0;
                      const ratioBg = isHighlightedMonth ? "bg-blue-50/30" : "bg-slate-50/60";
                      return (
                        <Fragment key={`ratio2-${m.month}`}>
                          <td colSpan={4} className={`px-3 py-2 text-center numeric-value text-slate-500 border-r-2 border-slate-200 ${ratioBg}`}>
                            {ratio != null ? formatPercent(ratio) : "—"}
                          </td>
                        </Fragment>
                      );
                    })}
                  </tr>
                  <tr className="border-b border-slate-100 text-xs">
                    <td className="px-5 py-2 font-medium text-slate-600 sticky left-0 bg-slate-50 border-r-2 border-slate-200 z-20">
                      MQL / Won
                    </td>
                    {groupedMonths.map((m, i) => {
                      const mql = m.calculatedTotals.mql || 0;
                      const wins = m.calculatedTotals.wins || 0;
                      const ratio = wins > 0 ? (mql / wins).toFixed(1) : "—";
                      const isHighlightedMonth = i % 2 === 0;
                      const ratioBg = isHighlightedMonth ? "bg-blue-50/30" : "bg-slate-50/60";
                      return (
                        <Fragment key={`ratio3-${m.month}`}>
                          <td colSpan={4} className={`px-3 py-2 text-center numeric-value text-slate-500 border-r-2 border-slate-200 ${ratioBg}`}>
                            {ratio}
                          </td>
                        </Fragment>
                      );
                    })}
                  </tr>
                </tbody>
              </table>
            </div>
          </Panel>
        );
      })()}

      {(() => {
        const downloadMonthlyPaidChannelCSV = () => {
          const headers = [
            "Month", "Source", "Cost", "Cost/MQL", "Cost/SQL", "Cost/SQO", "CAC", 
            "MQL", "SQL", "SQO", "Won", "Revenue", "ROAS"
          ];
          const rows: (string | number)[][] = [];

          groupedMonths.forEach((m) => {
            const ps = m.paid_search_efficiency || {};
            const psCost = ps.google_spend || 0;
            const psMql = ps.paid_search_mql || 0;
            const psSql = ps.paid_search_sql || 0;
            const psSqo = ps.paid_search_sqo || 0;
            const psWins = ps.paid_search_wins || 0;
            const psRev = ps.paid_search_revenue || 0;

            const soc = m.paid_social_efficiency || {};
            const socCost = soc.spend ?? soc.meta_spend ?? 0;
            const socMql = soc.paid_social_mql || 0;
            const socSql = soc.paid_social_sql || 0;
            const socSqo = soc.paid_social_sqo || 0;
            const socWins = soc.paid_social_wins || 0;
            const socRev = soc.paid_social_revenue || 0;

            const totalCost = psCost + socCost;
            const totalMql = psMql + socMql;
            const totalSql = psSql + socSql;
            const totalSqo = psSqo + socSqo;
            const totalWins = psWins + socWins;
            const totalRev = psRev + socRev;

            rows.push([
              m.label,
              "Paid Search",
              psCost,
              psMql ? (psCost / psMql).toFixed(2) : "—",
              psSql ? (psCost / psSql).toFixed(2) : "—",
              psSqo ? (psCost / psSqo).toFixed(2) : "—",
              psWins ? (psCost / psWins).toFixed(2) : "—",
              psMql,
              psSql,
              psSqo,
              psWins,
              psRev,
              psCost ? (psRev / psCost).toFixed(2) + "x" : "—"
            ]);

            rows.push([
              m.label,
              "Social",
              socCost,
              socMql ? (socCost / socMql).toFixed(2) : "—",
              socSql ? (socCost / socSql).toFixed(2) : "—",
              socSqo ? (socCost / socSqo).toFixed(2) : "—",
              socWins ? (socCost / socWins).toFixed(2) : "—",
              socMql,
              socSql,
              socSqo,
              socWins,
              socRev,
              socCost ? (socRev / socCost).toFixed(2) + "x" : "—"
            ]);

            rows.push([
              m.label,
              "Overall",
              totalCost,
              totalMql ? (totalCost / totalMql).toFixed(2) : "—",
              totalSql ? (totalCost / totalSql).toFixed(2) : "—",
              totalSqo ? (totalCost / totalSqo).toFixed(2) : "—",
              totalWins ? (totalCost / totalWins).toFixed(2) : "—",
              totalMql,
              totalSql,
              totalSqo,
              totalWins,
              totalRev,
              totalCost ? (totalRev / totalCost).toFixed(2) + "x" : "—"
            ]);
          });

          exportToCSV("monthly_paid_channel_performance.csv", headers, rows);
        };

        return (
          <Panel 
            title="Paid Channel Performance" 
            subtitle="Monthly spend, conversion cost, CAC, and ROAS for paid channels"
            right={
              <button
                onClick={downloadMonthlyPaidChannelCSV}
                className="px-2.5 py-1.5 text-xs font-medium text-slate-700 bg-white border border-slate-300 rounded-md hover:bg-slate-50 transition-colors inline-flex items-center gap-1.5"
              >
                <svg className="w-3.5 h-3.5 text-slate-500" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
                </svg>
                Export CSV
              </button>
            }
          >
            <div className="overflow-x-auto -mx-5 pb-4">
              <table className="w-full text-sm border-collapse min-w-max">
                {groupedMonths.map((m) => {
                  const ps = m.paid_search_efficiency || {};
                  const psCost = ps.google_spend || 0;
                  const psMql = ps.paid_search_mql || 0;
                  const psSql = ps.paid_search_sql || 0;
                  const psSqo = ps.paid_search_sqo || 0;
                  const psWins = ps.paid_search_wins || 0;
                  const psRev = ps.paid_search_revenue || 0;

                  const soc = m.paid_social_efficiency || {};
                  const socCost = soc.spend ?? soc.meta_spend ?? 0;
                  const socMql = soc.paid_social_mql || 0;
                  const socSql = soc.paid_social_sql || 0;
                  const socSqo = soc.paid_social_sqo || 0;
                  const socWins = soc.paid_social_wins || 0;
                  const socRev = soc.paid_social_revenue || 0;

                  const totalCost = psCost + socCost;
                  const totalMql = psMql + socMql;
                  const totalSql = psSql + socSql;
                  const totalSqo = psSqo + socSqo;
                  const totalWins = psWins + socWins;
                  const totalRev = psRev + socRev;

                  return (
                    <tbody key={`paid-${m.month}`} className="border-b-4 border-slate-200">
                      <tr className="bg-emerald-100/70 border-b border-emerald-200">
                        <td colSpan={12} className="px-5 py-2 font-semibold text-emerald-900 text-left sticky left-0 z-20 bg-emerald-100/70">
                          {m.label}
                        </td>
                      </tr>
                      <tr className="bg-emerald-50 text-slate-700 text-xs border-b border-emerald-100">
                        <th className="font-semibold px-5 py-2 text-left sticky left-0 z-20 bg-emerald-50 border-r-2 border-emerald-100">Source</th>
                        <th className="font-semibold px-3 py-2 text-right">Cost</th>
                        <th className="font-semibold px-3 py-2 text-right">Cost/MQL</th>
                        <th className="font-semibold px-3 py-2 text-right">Cost/SQL</th>
                        <th className="font-semibold px-3 py-2 text-right">Cost/SQO</th>
                        <th className="font-semibold px-3 py-2 text-right border-r-2 border-emerald-100">CAC</th>
                        <th className="font-semibold px-3 py-2 text-right">MQL</th>
                        <th className="font-semibold px-3 py-2 text-right">SQL</th>
                        <th className="font-semibold px-3 py-2 text-right">SQO</th>
                        <th className="font-semibold px-3 py-2 text-right">Won</th>
                        <th className="font-semibold px-3 py-2 text-right">Revenue</th>
                        <th className="font-semibold px-3 py-2 text-right">ROAS</th>
                      </tr>
                      
                      <tr className="bg-white border-b border-slate-100 hover:bg-slate-50 group">
                        <td className="px-5 py-2.5 font-medium text-slate-700 sticky left-0 bg-white border-r-2 border-slate-200 group-hover:bg-slate-50 z-20">Paid Search</td>
                        <td className="px-3 py-2.5 text-right numeric-value text-slate-600">{formatCurrency(psCost)}</td>
                        <td className="px-3 py-2.5 text-right numeric-value text-slate-600">{psMql ? formatCurrency(psCost / psMql) : "—"}</td>
                        <td className="px-3 py-2.5 text-right numeric-value text-slate-600">{psSql ? formatCurrency(psCost / psSql) : "—"}</td>
                        <td className="px-3 py-2.5 text-right numeric-value text-slate-600">{psSqo ? formatCurrency(psCost / psSqo) : "—"}</td>
                        <td className="px-3 py-2.5 text-right numeric-value text-slate-600 border-r-2 border-slate-200">{psWins ? formatCurrency(psCost / psWins) : "—"}</td>
                        <td className="px-3 py-2.5 text-right numeric-value text-slate-600">{psMql}</td>
                        <td className="px-3 py-2.5 text-right numeric-value text-slate-600">{psSql}</td>
                        <td className="px-3 py-2.5 text-right numeric-value text-slate-600">{psSqo}</td>
                        <td className="px-3 py-2.5 text-right numeric-value text-slate-600">{psWins}</td>
                        <td className="px-3 py-2.5 text-right numeric-value text-slate-600">{formatCurrency(psRev, {compact: true})}</td>
                        <td className="px-3 py-2.5 text-right numeric-value text-slate-600">{psCost ? (psRev / psCost).toFixed(2) + "x" : "—"}</td>
                      </tr>

                      <tr className="bg-white border-b border-slate-100 hover:bg-slate-50 group">
                        <td className="px-5 py-2.5 font-medium text-slate-700 sticky left-0 bg-white border-r-2 border-slate-200 group-hover:bg-slate-50 z-20">Social</td>
                        <td className="px-3 py-2.5 text-right numeric-value text-slate-600">{formatCurrency(socCost)}</td>
                        <td className="px-3 py-2.5 text-right numeric-value text-slate-600">{socMql ? formatCurrency(socCost / socMql) : "—"}</td>
                        <td className="px-3 py-2.5 text-right numeric-value text-slate-600">{socSql ? formatCurrency(socCost / socSql) : "—"}</td>
                        <td className="px-3 py-2.5 text-right numeric-value text-slate-600">{socSqo ? formatCurrency(socCost / socSqo) : "—"}</td>
                        <td className="px-3 py-2.5 text-right numeric-value text-slate-600 border-r-2 border-slate-200">{socWins ? formatCurrency(socCost / socWins) : "—"}</td>
                        <td className="px-3 py-2.5 text-right numeric-value text-slate-600">{socMql}</td>
                        <td className="px-3 py-2.5 text-right numeric-value text-slate-600">{socSql}</td>
                        <td className="px-3 py-2.5 text-right numeric-value text-slate-600">{socSqo}</td>
                        <td className="px-3 py-2.5 text-right numeric-value text-slate-600">{socWins}</td>
                        <td className="px-3 py-2.5 text-right numeric-value text-slate-600">{formatCurrency(socRev, {compact: true})}</td>
                        <td className="px-3 py-2.5 text-right numeric-value text-slate-600">{socCost ? (socRev / socCost).toFixed(2) + "x" : "—"}</td>
                      </tr>

                      <tr className="bg-emerald-100/50 font-semibold border-b border-emerald-200">
                        <td className="px-5 py-2.5 text-emerald-900 sticky left-0 bg-emerald-50 border-r-2 border-emerald-200 z-20">Overall</td>
                        <td className="px-3 py-2.5 text-right numeric-value text-emerald-900">{formatCurrency(totalCost)}</td>
                        <td className="px-3 py-2.5 text-right numeric-value text-emerald-900">{totalMql ? formatCurrency(totalCost / totalMql) : "—"}</td>
                        <td className="px-3 py-2.5 text-right numeric-value text-emerald-900">{totalSql ? formatCurrency(totalCost / totalSql) : "—"}</td>
                        <td className="px-3 py-2.5 text-right numeric-value text-emerald-900">{totalSqo ? formatCurrency(totalCost / totalSqo) : "—"}</td>
                        <td className="px-3 py-2.5 text-right numeric-value text-emerald-900 border-r-2 border-emerald-200">{totalWins ? formatCurrency(totalCost / totalWins) : "—"}</td>
                        <td className="px-3 py-2.5 text-right numeric-value text-emerald-900">{totalMql}</td>
                        <td className="px-3 py-2.5 text-right numeric-value text-emerald-900">{totalSql}</td>
                        <td className="px-3 py-2.5 text-right numeric-value text-emerald-900">{totalSqo}</td>
                        <td className="px-3 py-2.5 text-right numeric-value text-emerald-900">{totalWins}</td>
                        <td className="px-3 py-2.5 text-right numeric-value text-emerald-900">{formatCurrency(totalRev, {compact: true})}</td>
                        <td className="px-3 py-2.5 text-right numeric-value text-emerald-900">{totalCost ? (totalRev / totalCost).toFixed(2) + "x" : "—"}</td>
                      </tr>
                    </tbody>
                  );
                })}
              </table>
            </div>
          </Panel>
        );
      })()}

      {/* ── Yearly Overall: Performance by Source ── */}
      {(() => {
        const yearlyBySource: Record<string, { mql: number; sql: number; sqo: number; wins: number }> = {};
        groupedMonths.forEach((m) => {
          COMPARISON_SOURCE_ORDER.forEach((src) => {
            if (!yearlyBySource[src]) yearlyBySource[src] = { mql: 0, sql: 0, sqo: 0, wins: 0 };
            const s = m.groupedSources[src];
            if (s) {
              yearlyBySource[src].mql += s.mql;
              yearlyBySource[src].sql += s.sql;
              yearlyBySource[src].sqo += s.sqo;
              yearlyBySource[src].wins += s.wins;
            }
          });
        });
        const yearlyTotal = { mql: 0, sql: 0, sqo: 0, wins: 0 };
        Object.values(yearlyBySource).forEach((s) => {
          yearlyTotal.mql += s.mql;
          yearlyTotal.sql += s.sql;
          yearlyTotal.sqo += s.sqo;
          yearlyTotal.wins += s.wins;
        });

        const periodLabel = mode === "monthly"
          ? `${groupedMonths[groupedMonths.length - 1]?.label ?? ""} – ${groupedMonths[0]?.label ?? ""}`
          : `Last ${weeks} weeks`;

        const downloadPerformanceBySourceCSV = () => {
          const headers = ["Source", "MQL", "SQL", "SQO", "Won", "%SQL", "Won/SQL", "MQL/Won"];
          const rows = COMPARISON_SOURCE_ORDER.map((src) => {
            const s = yearlyBySource[src] || { mql: 0, sql: 0, sqo: 0, wins: 0 };
            const sqlRate = safeRatio(s.sql, s.mql);
            const wonSql = safeRatio(s.wins, s.sql);
            const mqlWon = s.wins > 0 ? (s.mql / s.wins).toFixed(1) : "—";
            return [
              src,
              s.mql,
              s.sql,
              s.sqo,
              s.wins,
              sqlRate != null ? formatPercent(sqlRate) : "—",
              wonSql != null ? formatPercent(wonSql) : "—",
              mqlWon
            ];
          });
          rows.push([
            "Total",
            yearlyTotal.mql,
            yearlyTotal.sql,
            yearlyTotal.sqo,
            yearlyTotal.wins,
            safeRatio(yearlyTotal.sql, yearlyTotal.mql) != null ? formatPercent(safeRatio(yearlyTotal.sql, yearlyTotal.mql)!) : "—",
            safeRatio(yearlyTotal.wins, yearlyTotal.sql) != null ? formatPercent(safeRatio(yearlyTotal.wins, yearlyTotal.sql)!) : "—",
            yearlyTotal.wins > 0 ? (yearlyTotal.mql / yearlyTotal.wins).toFixed(1) : "—"
          ]);
          exportToCSV("yearly_performance_by_source.csv", headers, rows);
        };

        return (
          <Panel
            title="Yearly Overall — Performance by Source"
            subtitle={`Aggregated totals across ${periodLabel}`}
            right={
              <button
                onClick={downloadPerformanceBySourceCSV}
                className="px-2.5 py-1.5 text-xs font-medium text-slate-700 bg-white border border-slate-300 rounded-md hover:bg-slate-50 transition-colors inline-flex items-center gap-1.5"
              >
                <svg className="w-3.5 h-3.5 text-slate-500" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
                </svg>
                Export CSV
              </button>
            }
          >
            <div className="overflow-x-auto -mx-5 pb-4">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="bg-violet-50 text-slate-700 text-xs border-b border-violet-100">
                    <th className="font-semibold px-5 py-2.5 text-left sticky left-0 z-20 bg-violet-50 border-r-2 border-violet-100">Source</th>
                    <th className="font-semibold px-4 py-2.5 text-right">MQL</th>
                    <th className="font-semibold px-4 py-2.5 text-right">SQL</th>
                    <th className="font-semibold px-4 py-2.5 text-right">SQO</th>
                    <th className="font-semibold px-4 py-2.5 text-right">Won</th>
                    <th className="font-semibold px-4 py-2.5 text-right">%SQL</th>
                    <th className="font-semibold px-4 py-2.5 text-right">Won/SQL</th>
                    <th className="font-semibold px-4 py-2.5 text-right">MQL/Won</th>
                  </tr>
                </thead>
                <tbody>
                  {COMPARISON_SOURCE_ORDER.map((src) => {
                    const s = yearlyBySource[src] || { mql: 0, sql: 0, sqo: 0, wins: 0 };
                    const sqlRate = safeRatio(s.sql, s.mql);
                    const wonSql = safeRatio(s.wins, s.sql);
                    const mqlWon = s.wins > 0 ? (s.mql / s.wins).toFixed(1) : "—";
                    return (
                      <tr key={src} className="border-b border-slate-100 hover:bg-violet-50/40 group">
                        <td className="px-5 py-2.5 font-medium text-slate-700 sticky left-0 bg-white border-r-2 border-slate-200 group-hover:bg-violet-50/40 z-20">{src}</td>
                        <td className="px-4 py-2.5 text-right numeric-value text-slate-600">{s.mql}</td>
                        <td className="px-4 py-2.5 text-right numeric-value text-slate-600">{s.sql}</td>
                        <td className="px-4 py-2.5 text-right numeric-value text-slate-600">{s.sqo}</td>
                        <td className="px-4 py-2.5 text-right numeric-value font-semibold text-slate-700">{s.wins}</td>
                        <td className="px-4 py-2.5 text-right numeric-value text-slate-500 text-xs">{sqlRate != null ? formatPercent(sqlRate) : "—"}</td>
                        <td className="px-4 py-2.5 text-right numeric-value text-slate-500 text-xs">{wonSql != null ? formatPercent(wonSql) : "—"}</td>
                        <td className="px-4 py-2.5 text-right numeric-value text-slate-500 text-xs">{mqlWon}</td>
                      </tr>
                    );
                  })}

                  {/* Yearly total row */}
                  <tr className="border-t-2 border-violet-300 bg-violet-100/60 font-semibold">
                    <td className="px-5 py-3 text-violet-900 sticky left-0 bg-violet-100/60 border-r-2 border-violet-300 z-20">Total</td>
                    <td className="px-4 py-3 text-right numeric-value text-violet-900">{yearlyTotal.mql}</td>
                    <td className="px-4 py-3 text-right numeric-value text-violet-900">{yearlyTotal.sql}</td>
                    <td className="px-4 py-3 text-right numeric-value text-violet-900">{yearlyTotal.sqo}</td>
                    <td className="px-4 py-3 text-right numeric-value text-violet-900">{yearlyTotal.wins}</td>
                    <td className="px-4 py-3 text-right numeric-value text-violet-700 text-xs">
                      {safeRatio(yearlyTotal.sql, yearlyTotal.mql) != null ? formatPercent(safeRatio(yearlyTotal.sql, yearlyTotal.mql)!) : "—"}
                    </td>
                    <td className="px-4 py-3 text-right numeric-value text-violet-700 text-xs">
                      {safeRatio(yearlyTotal.wins, yearlyTotal.sql) != null ? formatPercent(safeRatio(yearlyTotal.wins, yearlyTotal.sql)!) : "—"}
                    </td>
                    <td className="px-4 py-3 text-right numeric-value text-violet-700 text-xs">
                      {yearlyTotal.wins > 0 ? (yearlyTotal.mql / yearlyTotal.wins).toFixed(1) : "—"}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </Panel>
        );
      })()}

      {/* ── Yearly Overall: Paid Channel Performance ── */}
      {(() => {
        let ytdPsCost = 0, ytdPsMql = 0, ytdPsSql = 0, ytdPsSqo = 0, ytdPsWins = 0, ytdPsRev = 0;
        let ytdSocCost = 0, ytdSocMql = 0, ytdSocSql = 0, ytdSocSqo = 0, ytdSocWins = 0, ytdSocRev = 0;

        groupedMonths.forEach((m) => {
          const ps = m.paid_search_efficiency || {};
          ytdPsCost += ps.google_spend || 0;
          ytdPsMql += ps.paid_search_mql || 0;
          ytdPsSql += ps.paid_search_sql || 0;
          ytdPsSqo += ps.paid_search_sqo || 0;
          ytdPsWins += ps.paid_search_wins || 0;
          ytdPsRev += ps.paid_search_revenue || 0;

          const soc = m.paid_social_efficiency || {};
          ytdSocCost += soc.spend ?? soc.meta_spend ?? 0;
          ytdSocMql += soc.paid_social_mql || 0;
          ytdSocSql += soc.paid_social_sql || 0;
          ytdSocSqo += soc.paid_social_sqo || 0;
          ytdSocWins += soc.paid_social_wins || 0;
          ytdSocRev += soc.paid_social_revenue || 0;
        });

        const ytdTotalCost = ytdPsCost + ytdSocCost;
        const ytdTotalMql = ytdPsMql + ytdSocMql;
        const ytdTotalSql = ytdPsSql + ytdSocSql;
        const ytdTotalSqo = ytdPsSqo + ytdSocSqo;
        const ytdTotalWins = ytdPsWins + ytdSocWins;
        const ytdTotalRev = ytdPsRev + ytdSocRev;

        const periodLabel = mode === "monthly"
          ? `${groupedMonths[groupedMonths.length - 1]?.label ?? ""} – ${groupedMonths[0]?.label ?? ""}`
          : `Last ${weeks} weeks`;

        const colClass = "px-3 py-2.5 text-right numeric-value";

        const downloadPaidChannelCSV = () => {
          const headers = [
            "Source", "Cost", "Cost/MQL", "Cost/SQL", "Cost/SQO", "CAC", 
            "MQL", "SQL", "SQO", "Won", "Revenue", "ROAS"
          ];
          const rows = [
            [
              "Paid Search",
              ytdPsCost,
              ytdPsMql ? (ytdPsCost / ytdPsMql).toFixed(2) : "—",
              ytdPsSql ? (ytdPsCost / ytdPsSql).toFixed(2) : "—",
              ytdPsSqo ? (ytdPsCost / ytdPsSqo).toFixed(2) : "—",
              ytdPsWins ? (ytdPsCost / ytdPsWins).toFixed(2) : "—",
              ytdPsMql,
              ytdPsSql,
              ytdPsSqo,
              ytdPsWins,
              ytdPsRev,
              ytdPsCost ? (ytdPsRev / ytdPsCost).toFixed(2) + "x" : "—"
            ],
            [
              "Social",
              ytdSocCost,
              ytdSocMql ? (ytdSocCost / ytdSocMql).toFixed(2) : "—",
              ytdSocSql ? (ytdSocCost / ytdSocSql).toFixed(2) : "—",
              ytdSocSqo ? (ytdSocCost / ytdSocSqo).toFixed(2) : "—",
              ytdSocWins ? (ytdSocCost / ytdSocWins).toFixed(2) : "—",
              ytdSocMql,
              ytdSocSql,
              ytdSocSqo,
              ytdSocWins,
              ytdSocRev,
              ytdSocCost ? (ytdSocRev / ytdSocCost).toFixed(2) + "x" : "—"
            ],
            [
              "Overall",
              ytdTotalCost,
              ytdTotalMql ? (ytdTotalCost / ytdTotalMql).toFixed(2) : "—",
              ytdTotalSql ? (ytdTotalCost / ytdTotalSql).toFixed(2) : "—",
              ytdTotalSqo ? (ytdTotalCost / ytdTotalSqo).toFixed(2) : "—",
              ytdTotalWins ? (ytdTotalCost / ytdTotalWins).toFixed(2) : "—",
              ytdTotalMql,
              ytdTotalSql,
              ytdTotalSqo,
              ytdTotalWins,
              ytdTotalRev,
              ytdTotalCost ? (ytdTotalRev / ytdTotalCost).toFixed(2) + "x" : "—"
            ]
          ];
          exportToCSV("yearly_paid_channel_performance.csv", headers, rows);
        };

        return (
          <Panel
            title="Yearly Overall — Paid Channel Performance"
            subtitle={`Aggregated spend, costs, and conversions across ${periodLabel}`}
            right={
              <button
                onClick={downloadPaidChannelCSV}
                className="px-2.5 py-1.5 text-xs font-medium text-slate-700 bg-white border border-slate-300 rounded-md hover:bg-slate-50 transition-colors inline-flex items-center gap-1.5"
              >
                <svg className="w-3.5 h-3.5 text-slate-500" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
                </svg>
                Export CSV
              </button>
            }
          >
            <div className="overflow-x-auto -mx-5 pb-4">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="bg-amber-50 text-slate-700 text-xs border-b border-amber-100">
                    <th className="font-semibold px-5 py-2.5 text-left sticky left-0 z-20 bg-amber-50 border-r-2 border-amber-100">Source</th>
                    <th className="font-semibold px-3 py-2.5 text-right">Cost</th>
                    <th className="font-semibold px-3 py-2.5 text-right">Cost/MQL</th>
                    <th className="font-semibold px-3 py-2.5 text-right">Cost/SQL</th>
                    <th className="font-semibold px-3 py-2.5 text-right">Cost/SQO</th>
                    <th className="font-semibold px-3 py-2.5 text-right border-r-2 border-amber-100">CAC</th>
                    <th className="font-semibold px-3 py-2.5 text-right">MQL</th>
                    <th className="font-semibold px-3 py-2.5 text-right">SQL</th>
                    <th className="font-semibold px-3 py-2.5 text-right">SQO</th>
                    <th className="font-semibold px-3 py-2.5 text-right">Won</th>
                    <th className="font-semibold px-3 py-2.5 text-right">Revenue</th>
                    <th className="font-semibold px-3 py-2.5 text-right">ROAS</th>
                  </tr>
                </thead>
                <tbody>
                  {/* Paid Search row */}
                  <tr className="border-b border-slate-100 hover:bg-amber-50/40 group">
                    <td className="px-5 py-2.5 font-medium text-slate-700 sticky left-0 bg-white border-r-2 border-slate-200 group-hover:bg-amber-50/40 z-20">Paid Search</td>
                    <td className={`${colClass} text-slate-600`}>{formatCurrency(ytdPsCost)}</td>
                    <td className={`${colClass} text-slate-600`}>{ytdPsMql ? formatCurrency(ytdPsCost / ytdPsMql) : "—"}</td>
                    <td className={`${colClass} text-slate-600`}>{ytdPsSql ? formatCurrency(ytdPsCost / ytdPsSql) : "—"}</td>
                    <td className={`${colClass} text-slate-600`}>{ytdPsSqo ? formatCurrency(ytdPsCost / ytdPsSqo) : "—"}</td>
                    <td className={`${colClass} text-slate-600 border-r-2 border-slate-200`}>{ytdPsWins ? formatCurrency(ytdPsCost / ytdPsWins) : "—"}</td>
                    <td className={`${colClass} text-slate-600`}>{ytdPsMql}</td>
                    <td className={`${colClass} text-slate-600`}>{ytdPsSql}</td>
                    <td className={`${colClass} text-slate-600`}>{ytdPsSqo}</td>
                    <td className={`${colClass} text-slate-600`}>{ytdPsWins}</td>
                    <td className={`${colClass} text-slate-600`}>{formatCurrency(ytdPsRev, { compact: true })}</td>
                    <td className={`${colClass} text-slate-600`}>{ytdPsCost ? (ytdPsRev / ytdPsCost).toFixed(2) + "x" : "—"}</td>
                  </tr>

                  {/* Social row */}
                  <tr className="border-b border-slate-100 hover:bg-amber-50/40 group">
                    <td className="px-5 py-2.5 font-medium text-slate-700 sticky left-0 bg-white border-r-2 border-slate-200 group-hover:bg-amber-50/40 z-20">Social</td>
                    <td className={`${colClass} text-slate-600`}>{formatCurrency(ytdSocCost)}</td>
                    <td className={`${colClass} text-slate-600`}>{ytdSocMql ? formatCurrency(ytdSocCost / ytdSocMql) : "—"}</td>
                    <td className={`${colClass} text-slate-600`}>{ytdSocSql ? formatCurrency(ytdSocCost / ytdSocSql) : "—"}</td>
                    <td className={`${colClass} text-slate-600`}>{ytdSocSqo ? formatCurrency(ytdSocCost / ytdSocSqo) : "—"}</td>
                    <td className={`${colClass} text-slate-600 border-r-2 border-slate-200`}>{ytdSocWins ? formatCurrency(ytdSocCost / ytdSocWins) : "—"}</td>
                    <td className={`${colClass} text-slate-600`}>{ytdSocMql}</td>
                    <td className={`${colClass} text-slate-600`}>{ytdSocSql}</td>
                    <td className={`${colClass} text-slate-600`}>{ytdSocSqo}</td>
                    <td className={`${colClass} text-slate-600`}>{ytdSocWins}</td>
                    <td className={`${colClass} text-slate-600`}>{formatCurrency(ytdSocRev, { compact: true })}</td>
                    <td className={`${colClass} text-slate-600`}>{ytdSocCost ? (ytdSocRev / ytdSocCost).toFixed(2) + "x" : "—"}</td>
                  </tr>

                  {/* Grand overall row */}
                  <tr className="border-t-2 border-amber-300 bg-amber-100/60 font-semibold">
                    <td className="px-5 py-3 text-amber-900 sticky left-0 bg-amber-100/60 border-r-2 border-amber-300 z-20">Overall</td>
                    <td className={`${colClass} text-amber-900`}>{formatCurrency(ytdTotalCost)}</td>
                    <td className={`${colClass} text-amber-900`}>{ytdTotalMql ? formatCurrency(ytdTotalCost / ytdTotalMql) : "—"}</td>
                    <td className={`${colClass} text-amber-900`}>{ytdTotalSql ? formatCurrency(ytdTotalCost / ytdTotalSql) : "—"}</td>
                    <td className={`${colClass} text-amber-900`}>{ytdTotalSqo ? formatCurrency(ytdTotalCost / ytdTotalSqo) : "—"}</td>
                    <td className={`${colClass} text-amber-900 border-r-2 border-amber-300`}>{ytdTotalWins ? formatCurrency(ytdTotalCost / ytdTotalWins) : "—"}</td>
                    <td className={`${colClass} text-amber-900`}>{ytdTotalMql}</td>
                    <td className={`${colClass} text-amber-900`}>{ytdTotalSql}</td>
                    <td className={`${colClass} text-amber-900`}>{ytdTotalSqo}</td>
                    <td className={`${colClass} text-amber-900`}>{ytdTotalWins}</td>
                    <td className={`${colClass} text-amber-900`}>{formatCurrency(ytdTotalRev, { compact: true })}</td>
                    <td className={`${colClass} text-amber-900`}>{ytdTotalCost ? (ytdTotalRev / ytdTotalCost).toFixed(2) + "x" : "—"}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </Panel>
        );
      })()}

      {mode === "monthly" && (
        <FathomSqlQualityComparison
          months={groupedMonths.map((m) => ({
            month: m.month,
            label: m.label,
            sqlCount: m.calculatedTotals.sql,
          }))}
        />
      )}
    </div>
  );
}
